import { css } from '@emotion/react';
import { InvoiceModel, InvoiceModelV3 } from '@frontend/api-invoices';
import { formatCentsToCurrency } from '@frontend/currency';
import { getTodaysDate } from '@frontend/date';
import { useTranslation } from '@frontend/i18n';
import { calculateRemainingBalanceWithSurcharge } from '@frontend/payments-invoice-calculations';
import { useCreatePaymentRequest } from '@frontend/person-invoice-create';
import { pendo } from '@frontend/tracking';
import { theme } from '@frontend/theme';
import { ConfirmationModal, ModalControlModalProps, Text } from '@frontend/design-system';

type ConfirmCreateRequestModalProps = ModalControlModalProps & {
  invoice: InvoiceModel;
  onCreateRequest: (createdRequest: InvoiceModelV3) => void;
};

export const ConfirmCreateRequestModal = ({ invoice, onCreateRequest, ...rest }: ConfirmCreateRequestModalProps) => {
  const { t } = useTranslation('payments');
  const { createPaymentRequest } = useCreatePaymentRequest({ locationId: invoice.locationId });

  const handleCreateInvoice = async () => {
    await createPaymentRequest({
      personId: invoice.person.id!,
      amount: Math.round(Number(calculateRemainingBalanceWithSurcharge(invoice))),
      mobileNumber: invoice.person.mobilePhone,
      memo: `Remainder requested from: ${invoice.id}`,
      providerName: invoice.providerName,
    }).then((createdInvoice) => {
      pendo.track('payments-create-invoice', {
        date: getTodaysDate('MM/DD/YYYY'),
        amount: Number(calculateRemainingBalanceWithSurcharge(createdInvoice)),
      });

      onCreateRequest(createdInvoice);
    });
  };

  return (
    <ConfirmationModal
      {...rest}
      onConfirm={handleCreateInvoice}
      title={'Request Remainder'}
      confirmLabel={t('Create Request')}
    >
      <div
        css={css`
          margin-top: ${theme.spacing(2)};
          margin-bottom: ${theme.spacing(2)};
        `}
      >
        <Text>{t('Request payment for the remainder of the original request?')}</Text>
        <div
          css={css`
            display: flex;
            flex-direction: row;
            gap: ${theme.spacing(1)};
            margin-top: ${theme.spacing(2)};
          `}
        >
          <Text>{t('Remainder amount: ')}</Text>
          <Text weight='semibold'>{formatCentsToCurrency(calculateRemainingBalanceWithSurcharge(invoice))}</Text>
        </div>
      </div>
    </ConfirmationModal>
  );
};
